/**
 * Reading and narrowing the board.
 *
 * The filters live in the URL so a triage call can be a pasted link --
 * "every blocking bug still open on Discover" is a URL, not a sentence
 * somebody has to re-create. Parsing is here rather than in the component
 * so the query-string handling is testable and so an unknown value in the
 * URL degrades to "no filter" instead of an empty board nobody can explain.
 *
 * Pure.
 */

import {
  CLOSED_STATES,
  IMPACT_META,
  OPEN_STATES,
  STATE_ORDER,
  TYPE_META,
  impactMeta,
  stateMeta,
  typeMeta,
} from './feedbackTypes';
import { screenLabel } from './screens';
import { toCsv } from '../../../utils/csv';

/** `status` takes a state id, or one of these two groupings. */
export const OPEN = 'open';
export const CLOSED = 'closed';

export const SORT = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
  IMPACT: 'impact',
  UPDATED: 'updated',
};

export const SORT_LABELS = {
  [SORT.NEWEST]: 'Newest first',
  [SORT.OLDEST]: 'Oldest first',
  [SORT.IMPACT]: 'Most blocking first',
  [SORT.UPDATED]: 'Recently updated',
};

export const AUDIENCE = { ALL: '', BRAND: 'brand', INTERNAL: 'internal' };

export const EMPTY_FILTERS = {
  status: OPEN,
  type: '',
  impact: '',
  screen: '',
  audience: AUDIENCE.ALL,
  q: '',
  sort: SORT.IMPACT,
};

const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';
const text = (value) => (isBlank(value) ? '' : String(value).trim());
const lower = (value) => text(value).toLowerCase();

const STATUS_VALUES = [OPEN, CLOSED, ...STATE_ORDER];

/**
 * Filters from a location.search.
 *
 * Anything unrecognised is dropped rather than honoured, so a hand-edited
 * or stale link shows a sensible board instead of nothing at all.
 */
export function filtersFromSearch(search, screenIds) {
  let params;
  try {
    params = new URLSearchParams(String(search || ''));
  } catch (e) {
    return { ...EMPTY_FILTERS };
  }

  const known = Array.isArray(screenIds) ? screenIds.filter(Boolean) : [];
  const pick = (key, allowed, fallback) => {
    const value = text(params.get(key));
    return allowed.indexOf(value) !== -1 ? value : fallback;
  };

  return {
    status: pick('status', STATUS_VALUES, OPEN),
    type: pick('type', Object.keys(TYPE_META), ''),
    impact: pick('impact', Object.keys(IMPACT_META), ''),
    screen: pick('screen', known, ''),
    audience: pick('audience', [AUDIENCE.BRAND, AUDIENCE.INTERNAL], AUDIENCE.ALL),
    q: text(params.get('q')).slice(0, 120),
    sort: pick('sort', Object.values(SORT), SORT.IMPACT),
  };
}

/** The inverse, with defaults left out so a plain board has a plain URL. */
export function searchFromFilters(filters) {
  const row = { ...EMPTY_FILTERS, ...(filters || {}) };
  const params = new URLSearchParams();
  Object.keys(EMPTY_FILTERS).forEach((key) => {
    const value = text(row[key]);
    if (value && value !== EMPTY_FILTERS[key]) params.set(key, value);
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** Staff submissions and brand submissions read very differently in triage. */
export function audienceOf(item) {
  const by = (item && item.submitted_by) || {};
  return by.is_staff ? AUDIENCE.INTERNAL : AUDIENCE.BRAND;
}

const matchesStatus = (item, status) => {
  if (!status) return true;
  if (status === OPEN) return OPEN_STATES.indexOf(item.state) !== -1;
  if (status === CLOSED) return CLOSED_STATES.indexOf(item.state) !== -1;
  return item.state === status;
};

const matchesQuery = (item, q) => {
  if (!q) return true;
  const needle = lower(q);
  const by = item.submitted_by || {};
  return [item.title, item.detail, item.reply, by.name, by.email, screenLabel(item.screen)]
    .map(lower)
    .some((haystack) => haystack.indexOf(needle) !== -1);
};

export function filterItems(items, filters) {
  const list = Array.isArray(items) ? items : [];
  const row = { ...EMPTY_FILTERS, ...(filters || {}) };

  return list.filter(
    (item) =>
      item &&
      matchesStatus(item, row.status) &&
      (!row.type || item.type === row.type) &&
      (!row.impact || item.impact === row.impact) &&
      (!row.screen || item.screen === row.screen) &&
      (!row.audience || audienceOf(item) === row.audience) &&
      matchesQuery(item, row.q),
  );
}

const time = (value) => {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** Sorting never mutates: callers hold the fetched list. */
export function sortItems(items, sort) {
  const list = Array.isArray(items) ? [...items] : [];
  const byNewest = (a, b) => time(b.created_at) - time(a.created_at);

  if (sort === SORT.OLDEST) return list.sort((a, b) => time(a.created_at) - time(b.created_at));
  if (sort === SORT.UPDATED) {
    return list.sort(
      (a, b) => time(b.updated_at || b.created_at) - time(a.updated_at || a.created_at),
    );
  }
  if (sort === SORT.IMPACT) {
    // Equal impact falls back to age, so the oldest blocking item is the one
    // at the top rather than whichever happened to be fetched first.
    return list.sort(
      (a, b) => impactMeta(a.impact).rank - impactMeta(b.impact).rank || byNewest(a, b),
    );
  }
  return list.sort(byNewest);
}

/** How many sit in each state, for the tab counts. Unfiltered on purpose. */
export function countsByState(items) {
  const list = Array.isArray(items) ? items : [];
  const counts = STATE_ORDER.reduce((acc, state) => ({ ...acc, [state]: 0 }), {});
  const totals = list.reduce(
    (acc, item) => {
      if (!item) return acc;
      const state = item.state;
      const next = { ...acc };
      if (next[state] !== undefined) next[state] += 1;
      if (OPEN_STATES.indexOf(state) !== -1) next[OPEN] += 1;
      if (CLOSED_STATES.indexOf(state) !== -1) next[CLOSED] += 1;
      return next;
    },
    { ...counts, [OPEN]: 0, [CLOSED]: 0 },
  );
  return totals;
}

/**
 * Which screens hurt, loudest first.
 *
 * This is the question the board exists to answer and no single item can:
 * one complaint about Discover is noise, fourteen is a decision.
 */
export function groupByScreen(items) {
  const list = Array.isArray(items) ? items : [];
  const seen = list.reduce((acc, item) => {
    if (!item) return acc;
    const id = text(item.screen) || 'other';
    const previous = acc[id] || { id, label: screenLabel(id), total: 0, blocking: 0, open: 0 };
    return {
      ...acc,
      [id]: {
        ...previous,
        total: previous.total + 1,
        blocking: previous.blocking + (item.impact === 'blocking' ? 1 : 0),
        open: previous.open + (OPEN_STATES.indexOf(item.state) !== -1 ? 1 : 0),
      },
    };
  }, {});

  return Object.keys(seen)
    .map((id) => seen[id])
    .sort((a, b) => b.open - a.open || b.total - a.total || a.label.localeCompare(b.label));
}

export const BOARD_COLUMNS = [
  { key: 'id', header: 'ID' },
  { key: 'title', header: 'Title' },
  { key: 'type', header: 'Type' },
  { key: 'impact', header: 'Impact' },
  { key: 'state', header: 'Status' },
  { key: 'screen', header: 'Screen' },
  { key: 'path', header: 'Path' },
  { key: 'audience', header: 'From' },
  { key: 'submitted_by', header: 'Submitted by' },
  { key: 'owner', header: 'Owner' },
  { key: 'created_at', header: 'Raised' },
  { key: 'updated_at', header: 'Last moved' },
  { key: 'detail', header: 'Detail' },
  { key: 'reply', header: 'Reply' },
];

function cellOf(item, key) {
  const by = item.submitted_by || {};
  switch (key) {
    case 'type':
      return typeMeta(item.type).label;
    case 'impact':
      return impactMeta(item.impact).label;
    case 'state':
      return stateMeta(item.state).label;
    case 'screen':
      return screenLabel(item.screen);
    case 'audience':
      return audienceOf(item) === AUDIENCE.INTERNAL ? 'Internal' : 'Brand';
    case 'submitted_by':
      return text(by.name) || text(by.email);
    case 'owner':
      return text(item.owner_name) || text(item.owner);
    default:
      return text(item[key]);
  }
}

/** What the board is showing, for a planning session away from the screen. */
export function boardCsv(items) {
  const list = Array.isArray(items) ? items : [];
  return toCsv([
    BOARD_COLUMNS.map((column) => column.header),
    ...list.map((item) => BOARD_COLUMNS.map((column) => cellOf(item || {}, column.key))),
  ]);
}

export function boardCsvName() {
  return `feedback-${new Date().toISOString().slice(0, 10)}.csv`;
}
